"use client"
import { useEffect, useRef } from "react"

const HUBSPOT_PORTAL_ID = "245315973"
const HUBSPOT_REGION = "na2"

/**
 * Placeholder for a HubSpot form.
 *
 * The portal embed script (loaded once in the root layout) discovers forms two
 * ways: a `getElementsByClassName` sweep when it initialises, and a
 * MutationObserver for anything added afterwards. The observer only descends
 * five levels into each added node, and the footer's placeholder sits deeper
 * than that inside the page module tree — so a footer that hydrates after the
 * script initialises used to be missed, intermittently, depending on which
 * ancestor React happened to insert.
 *
 * Appending the placeholder ourselves makes it the added node rather than a
 * descendant of one, so the depth limit never applies. If the script
 * initialises after this runs, the sweep finds it instead. Either order works.
 *
 * HubSpot binds each `.hs-form-frame` placeholder once, at script init, by
 * matching `data-form-id` + `data-portal-id`. Multiple placeholders can
 * safely share a form id - verified that two placeholders with the same id
 * both bind correctly when both are present at init (see ContactModal.tsx,
 * which shares a form id with the footer). The actual constraint is timing:
 * a placeholder added after init gets a dead iframe (no src), so every
 * HubspotForm must mount at hydration and stay mounted for the page's
 * lifetime - never conditionally render or remount one.
 */
export const HubspotForm = ({
  formId,
  portalId = HUBSPOT_PORTAL_ID,
  region = HUBSPOT_REGION,
}: {
  formId: string
  portalId?: string
  region?: string
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const frame = document.createElement("div")
    frame.className = "hs-form-frame"
    frame.dataset.region = region
    frame.dataset.formId = formId
    frame.dataset.portalId = portalId
    container.appendChild(frame)

    return () => frame.remove()
  }, [formId, portalId, region])

  return <div ref={containerRef} />
}
